import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMember } from '@/hooks/useMember';
import { GenderEnum } from '@/types/member.types';
import SBack from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { SelectCountryDrawer } from '@/components/ui/organisms/SelectCountryDrawer';
import { SelectStateDrawer } from '@/components/ui/organisms/SelectStateDrawer';
import { SelectGenderDrawer } from '@/components/ui/organisms/SelectGenderDrawer';
import './EditProfile.css';

const GENDER_LABELS: Record<string, string> = {
  [GenderEnum.MALE]:        'Male',
  [GenderEnum.FEMALE]:      'Female',
  [GenderEnum.UNSPECIFIED]: 'Unspecified',
};

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const { memberProfile, isLoading, updateProfile, isUpdating } = useMember();

  const [form, setForm] = useState({
    firstName: '', lastName: '', gender: undefined as string | undefined,
    birthDate: '', country: '', line1: '', city: '', state: '', postalCode: '',
  });

  const [showCountry, setShowCountry] = useState(false);
  const [showState,   setShowState]   = useState(false);
  const [showGender,  setShowGender]  = useState(false);

  useEffect(() => {
    if (memberProfile) {
      setForm({
        firstName: memberProfile.firstName || '',
        lastName:  memberProfile.lastName  || '',
        gender:    memberProfile.gender,
        birthDate: memberProfile.dob || '',
        country:   memberProfile.address?.country    || '',
        line1:     memberProfile.address?.line1      || '',
        city:      memberProfile.address?.city       || '',
        state:     memberProfile.address?.state      || '',
        postalCode:memberProfile.address?.postalCode || '',
      });
    }
  }, [memberProfile]);

  const set = (field: string, value: string | undefined) =>
    setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = () => {
    updateProfile({
      firstName: form.firstName,
      lastName:  form.lastName,
      gender:    form.gender,
      dob:       form.birthDate,
      address: {
        line1: form.line1, city: form.city, state: form.state,
        postalCode: form.postalCode, country: form.country,
      },
    }, { onSuccess: () => navigate('/profile/personal-information') });
  };

  return (
    <div className="ep">
      <div className="ep__header">
        <SBack onClick={() => navigate(-1)} />
        <span className="ep__title">Edit Profile</span>
        <ThemeToggle />
      </div>

      {isLoading ? (
        <div className="ep__loading">Loading…</div>
      ) : (
        <div className="ep__body">

          {/* Name */}
          <div>
            <p className="ep__section-label">Name</p>
            <div className="ep__card">
              <div className="ep__field">
                <span className="ep__field-label">First Name</span>
                <input className="ep__input" placeholder="First name"
                  value={form.firstName} onChange={e => set('firstName', e.target.value)} />
              </div>
              <div className="ep__divider" />
              <div className="ep__field">
                <span className="ep__field-label">Last Name</span>
                <input className="ep__input" placeholder="Last name"
                  value={form.lastName} onChange={e => set('lastName', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Contact (read-only) */}
          <div>
            <p className="ep__section-label">Contact</p>
            <div className="ep__card">
              <div className="ep__field">
                <span className="ep__field-label">Email</span>
                <input className="ep__input ep__input--readonly" readOnly
                  value={memberProfile?.email || ''} onChange={() => {}} />
              </div>
              {memberProfile?.mobile && (
                <>
                  <div className="ep__divider" />
                  <div className="ep__field">
                    <span className="ep__field-label">Mobile</span>
                    <input className="ep__input ep__input--readonly" readOnly
                      value={memberProfile.mobile} onChange={() => {}} />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Gender */}
          <div>
            <p className="ep__section-label">Gender</p>
            <div className="ep__card">
              <div className="ep__field ep__field--tappable" onClick={() => setShowGender(true)}>
                <span className="ep__field-label">Gender</span>
                <input className="ep__input" readOnly placeholder="Select gender"
                  value={form.gender ? GENDER_LABELS[form.gender] || '' : ''} onChange={() => {}} />
              </div>
            </div>
          </div>

          {/* Birth date */}
          <div>
            <p className="ep__section-label">Date of Birth</p>
            <div className="ep__card">
              <div className="ep__field">
                <span className="ep__field-label">Date of Birth</span>
                <input type="date" className="ep__input"
                  value={form.birthDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => set('birthDate', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <p className="ep__section-label">Address</p>
            <div className="ep__card">
              <div className="ep__field ep__field--tappable" onClick={() => setShowCountry(true)}>
                <span className="ep__field-label">Country</span>
                <input className="ep__input" readOnly placeholder="Select country"
                  value={form.country} onChange={() => {}} />
              </div>
              <div className="ep__divider" />
              <div className="ep__field">
                <span className="ep__field-label">Street Address</span>
                <input className="ep__input" placeholder="Street address"
                  value={form.line1} onChange={e => set('line1', e.target.value)} />
              </div>
              <div className="ep__divider" />
              <div className="ep__field">
                <span className="ep__field-label">City</span>
                <input className="ep__input" placeholder="City"
                  value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="ep__divider" />
              <div className="ep__row">
                <div
                  className={`ep__field${form.country === 'United States' ? ' ep__field--tappable' : ''}`}
                  onClick={form.country === 'United States' ? () => setShowState(true) : undefined}
                >
                  <span className="ep__field-label">State</span>
                  {form.country === 'United States' ? (
                    <input className="ep__input" readOnly placeholder="Select state"
                      value={form.state} onChange={() => {}} />
                  ) : (
                    <input className="ep__input" placeholder="State"
                      value={form.state} onChange={e => set('state', e.target.value)} />
                  )}
                </div>
                <div className="ep__field">
                  <span className="ep__field-label">Postal Code</span>
                  <input className="ep__input" placeholder="Postal code"
                    value={form.postalCode} onChange={e => set('postalCode', e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <button className="ep__cta" onClick={handleSubmit} disabled={isUpdating}>
            {isUpdating ? 'Saving…' : 'Save Changes'}
          </button>

        </div>
      )}

      <SelectGenderDrawer
        isOpen={showGender} onClose={() => setShowGender(false)}
        currentGender={form.gender} onGenderSelect={g => set('gender', g)} />
      <SelectCountryDrawer
        isOpen={showCountry} onClose={() => setShowCountry(false)}
        currentCountry={form.country}
        onCountrySelect={c => {
          set('country', c);
          if (form.country === 'United States' && c !== 'United States') set('state', '');
        }} />
      <SelectStateDrawer
        isOpen={showState && form.country === 'United States'} onClose={() => setShowState(false)}
        currentState={form.state} onStateSelect={s => set('state', s)} />
    </div>
  );
};

export default EditProfile;
